import {
  createAccountDeletionFrontendPlugin,
  type FrontendPlugin,
} from '@xhubio-saas/core-frontend/kernel'
import {
  localesBg as logAuditLocalesBg,
  localesCs as logAuditLocalesCs,
  localesDe as logAuditLocalesDe,
  localesEn as logAuditLocalesEn,
  localesEs as logAuditLocalesEs,
  localesFr as logAuditLocalesFr,
  localesHu as logAuditLocalesHu,
  localesIt as logAuditLocalesIt,
  localesNl as logAuditLocalesNl,
  localesPl as logAuditLocalesPl,
  localesPt as logAuditLocalesPt,
  localesRo as logAuditLocalesRo,
} from '@xhubio-saas/core-frontend/plugin-log-audit'
import { onboardingYamlMessages } from '@xhubio-saas/core-frontend/plugin-onboarding-yaml'
import {
  localesBg as rolesLocalesBg,
  localesCs as rolesLocalesCs,
  localesDe as rolesLocalesDe,
  localesEn as rolesLocalesEn,
  localesEs as rolesLocalesEs,
  localesFr as rolesLocalesFr,
  localesHu as rolesLocalesHu,
  localesIt as rolesLocalesIt,
  localesNl as rolesLocalesNl,
  localesPl as rolesLocalesPl,
  localesPt as rolesLocalesPt,
  localesRo as rolesLocalesRo,
} from '@xhubio-saas/core-frontend/plugin-roles'
import {
  localesBg as subscriptionLocalesBg,
  localesCs as subscriptionLocalesCs,
  localesDe as subscriptionLocalesDe,
  localesEn as subscriptionLocalesEn,
  localesEs as subscriptionLocalesEs,
  localesFr as subscriptionLocalesFr,
  localesHu as subscriptionLocalesHu,
  localesIt as subscriptionLocalesIt,
  localesNl as subscriptionLocalesNl,
  localesPl as subscriptionLocalesPl,
  localesPt as subscriptionLocalesPt,
  localesRo as subscriptionLocalesRo,
} from '@xhubio-saas/core-frontend/plugin-subscription'
import {
  bg as usageLocalesBg,
  cs as usageLocalesCs,
  de as usageLocalesDe,
  en as usageLocalesEn,
  es as usageLocalesEs,
  fr as usageLocalesFr,
  hu as usageLocalesHu,
  it as usageLocalesIt,
  nl as usageLocalesNl,
  pl as usageLocalesPl,
  pt as usageLocalesPt,
  ro as usageLocalesRo,
} from '@xhubio-saas/core-frontend/plugin-usage'
import {
  localesBg as userLocalesBg,
  localesCs as userLocalesCs,
  localesDe as userLocalesDe,
  localesEn as userLocalesEn,
  localesEs as userLocalesEs,
  localesFr as userLocalesFr,
  localesHu as userLocalesHu,
  localesIt as userLocalesIt,
  localesNl as userLocalesNl,
  localesPl as userLocalesPl,
  localesPt as userLocalesPt,
  localesRo as userLocalesRo,
} from '@xhubio-saas/core-frontend/plugin-user'
import {
  localesBg as apiKeyLocalesBg,
  localesCs as apiKeyLocalesCs,
  localesDe as apiKeyLocalesDe,
  localesEn as apiKeyLocalesEn,
  localesEs as apiKeyLocalesEs,
  localesFr as apiKeyLocalesFr,
  localesHu as apiKeyLocalesHu,
  localesIt as apiKeyLocalesIt,
  localesNl as apiKeyLocalesNl,
  localesPl as apiKeyLocalesPl,
  localesPt as apiKeyLocalesPt,
  localesRo as apiKeyLocalesRo,
} from '@xhubio-saas/frontend-plugin-api-key'
import {
  localesBg as pdfLocalesBg,
  localesCs as pdfLocalesCs,
  localesDe as pdfLocalesDe,
  localesEn as pdfLocalesEn,
  localesEs as pdfLocalesEs,
  localesFr as pdfLocalesFr,
  localesHu as pdfLocalesHu,
  localesIt as pdfLocalesIt,
  localesNl as pdfLocalesNl,
  localesPl as pdfLocalesPl,
  localesPt as pdfLocalesPt,
  localesRo as pdfLocalesRo,
} from '@xhubio-saas/pdf-frontend/plugin-pdf'
import { Navigate, useParams } from 'react-router-dom'
import {
  AcceptInvitationPageWithProviders,
  AcceptOwnershipTransferPageWithProviders,
  ApiKeysPageWithProviders,
  ChangelogPageWithProviders,
  CookieSettingsPageWithProviders,
  LegalSettingsPageWithProviders,
  LogAuditPageWithProviders,
  OnboardingYamlPageWithProviders,
  PdfTemplateListV2PageWithProviders,
  RoleDetailPageWithProviders,
  RoleListPageWithProviders,
  SettingsAccountPageWithProviders,
  SettingsSystemPageWithProviders,
  SubscriptionPageWithProviders,
  UsagePageWithProviders,
  UserDetailPageWithProviders,
  UserListPageWithProviders,
  VerifyEmailChangePageWithProviders,
} from './app.pages'
import { OnboardingThanks } from './components/OnboardingThanks'
import { PdfTemplateEditorV2Page } from './pages/PdfTemplateEditorV2Page'

/**
 * Plan PDF-API 09-05/01: der alte Craft.js-Editor ist abgeschaltet. Die drei
 * v1-Routen bleiben als Weiterleitung stehen, damit Lesezeichen und offene
 * Tabs nicht auf einer weissen Seite landen. Muster: dashandwerk
 * `app.plugins.tsx` (`PdfTemplatesRedirect`). Bewusst OHNE
 * `requiredEntitlement` und OHNE Breadcrumb — eine Weiterleitung darf nicht
 * am Recht scheitern, das Ziel prueft selbst.
 */
const PdfTemplatesRedirect = () => <Navigate to="/pdf/v2/templates" replace />

/**
 * `/pdf/templates/:id` → derselbe Datensatz im neuen Editor. `new` ist im
 * neuen Editor keine Route (Anlegen laeuft ueber den Dialog der Liste),
 * deshalb landet genau dieser Wert auf der Liste.
 */
const PdfTemplateEditRedirect = () => {
  const { id } = useParams<{ id: string }>()
  return (
    <Navigate to={id && id !== 'new' ? `/pdf/v2/templates/${id}` : '/pdf/v2/templates'} replace />
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Plugin Registration
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Application Plugins
 *
 * Register your frontend plugins here. Each plugin can add:
 * - Navigation items
 * - Dashboard widgets
 * - Settings tabs
 * - Routes
 * - Quick actions
 * - Translations
 *
 * Note: requiredEntitlement should always be set for proper RBAC access control.
 * Entitlements are defined in the corresponding kernel-plugin (e.g., kernel-plugin-api-key).
 */
/**
 * Absolute URL of the backend-served OpenAPI docs (Scalar UI at `/docs`).
 * Resolved from runtime config → build-time env → relative `/docs`. The
 * sidebar entry links here directly and opens it in a new browser tab.
 */
const OPENAPI_DOCS_URL = (() => {
  const runtimeApi = window.__RUNTIME_CONFIG__?.API_URL?.replace(/\/+$/, '')
  const buildApi = import.meta.env.VITE_API_URL?.replace(/\/+$/, '')
  const apiUrl = runtimeApi || buildApi || ''
  return `${apiUrl}/docs`
})()

export const plugins: FrontendPlugin[] = [
  // Quick Actions (Cmd+K palette) — navigation shortcuts to the main
  // console pages. Labels reuse the existing (already-translated in all
  // locales) navigation labelKeys; entitlements mirror the nav entries so
  // an action only appears when the user may reach the page.
  {
    name: '@xhubio-saas/saas-pdf-api-frontend/plugin-quick-actions',
    version: '1.0.0',
    quickActions: [
      {
        id: 'goto-usage',
        labelKey: 'usage.nav.title',
        icon: 'Activity',
        href: '/usage',
        requiredEntitlement: 'billing:usage:read',
      },
      {
        id: 'goto-api-keys',
        labelKey: 'apiKey.nav.title',
        icon: 'KeyRound',
        href: '/api-keys',
        requiredEntitlement: 'api-key:api-key:read',
      },
      {
        id: 'goto-pdf-templates',
        labelKey: 'pdf.templates.title',
        icon: 'FileType',
        href: '/pdf/v2/templates',
        requiredEntitlement: 'pdf:template:read',
      },
      {
        id: 'goto-log-audit',
        labelKey: 'logAudit.nav.title',
        icon: 'ClipboardList',
        href: '/log-audit',
        requiredEntitlement: 'audit:log:read',
      },
      {
        id: 'goto-users',
        labelKey: 'user.nav.title',
        icon: 'Users',
        href: '/users',
        requiredEntitlement: 'members:member:read',
      },
      {
        id: 'goto-roles',
        labelKey: 'roles.settings.tab',
        icon: 'ShieldCheck',
        href: '/settings/roles',
        requiredEntitlement: 'rbac:role:read',
      },
      {
        id: 'goto-subscription',
        labelKey: 'subscription.nav.title',
        icon: 'CreditCard',
        href: '/subscription',
        requiredEntitlement: 'billing:subscription:read',
      },
      {
        id: 'goto-settings',
        labelKey: 'common:common.myAccount',
        icon: 'UserCog',
        href: '/settings',
      },
      {
        id: 'goto-system',
        labelKey: 'common:settings.system.tab',
        icon: 'Settings',
        href: '/system',
      },
      {
        id: 'goto-changelog',
        labelKey: 'common:settings.system.changelogTitle',
        icon: 'ScrollText',
        href: '/changelog',
      },
      {
        id: 'goto-cookie-settings',
        labelKey: 'common:cookieConsent.footer.cookieSettings',
        icon: 'Cookie',
        href: '/settings/cookies',
      },
    ],
  },

  // Core Navigation Items
  {
    name: '@xhubio-saas/webapp-core-navigation',
    version: '1.0.0',
    navigation: [
      {
        // „System" der Organisation (Versionen, Rechtliches). Das Persoenliche
        // liegt am Avatar („Mein Konto", `/settings`); hier stand bis
        // 2026-09-25 „Einstellungen" in der Gruppe „Einstellungen". `prefix`:
        // auch die Unterseiten `/system/...` markieren den Eintrag.
        id: 'system',
        labelKey: 'common:settings.system.tab',
        icon: 'Settings',
        href: '/system',
        activeMatch: 'prefix',
        order: 40,
        group: 'settings',
      },
      {
        // Änderungsprotokoll — letzter Eintrag der letzten Gruppe (= ganz
        // unten in der Hauptsidebar). Vorher eine eingeklappte Card auf
        // Einstellungen → System.
        id: 'changelog',
        labelKey: 'common:settings.system.changelogTitle',
        icon: 'ScrollText',
        href: '/changelog',
        order: 60,
        group: 'settings',
      },
    ],
    routes: [
      {
        path: '/settings/account',
        component: SettingsAccountPageWithProviders,
        layout: 'protected',
        breadcrumb: {
          labelKey: 'settings.account.tab',
        },
      },
      {
        path: '/system',
        component: SettingsSystemPageWithProviders,
        layout: 'protected',
        breadcrumb: {
          labelKey: 'settings.system.tab',
        },
      },
      {
        path: '/changelog',
        component: ChangelogPageWithProviders,
        layout: 'protected',
        breadcrumb: {
          labelKey: 'settings.system.changelogTitle',
        },
      },
      {
        path: '/settings/cookies',
        component: CookieSettingsPageWithProviders,
        layout: 'protected',
        breadcrumb: {
          labelKey: 'cookieConsent.settings.title',
        },
      },
      {
        path: '/system/legal',
        component: LegalSettingsPageWithProviders,
        layout: 'protected',
        breadcrumb: {
          labelKey: 'settings.legal.tab',
        },
      },
    ],
    // Cookie-Einstellungen als Tab in der Settings-Navigation (nicht in der
    // Hauptsidebar): öffnet beim Ankommen den Consent-Dialog, die Seite
    // dahinter ist Landeplatz. Vorher ein Button in der „Rechtliches"-Card auf
    // Einstellungen → System. Tab-Label ist das kurze „Cookie"
    // (settings.cookies.tab, app-Locales); Seiten-Copy aus dem Kernel-Bundle
    // (cookieConsent.*, alle 13 Sprachen).
    settingsTabs: [
      {
        id: 'cookies',
        labelKey: 'settings.cookies.tab',
        icon: 'Cookie',
        href: '/settings/cookies',
        order: 60,
        component: CookieSettingsPageWithProviders,
      },
      {
        // Rechtliches (Datenschutz/AGB/Impressum) — wie „Cookie" ein eigener
        // Tab; vorher eine Card auf Einstellungen → System (davor App-Footer).
        id: 'legal',
        labelKey: 'settings.legal.tab',
        icon: 'Scale',
        href: '/system/legal',
        order: 70,
        component: LegalSettingsPageWithProviders,
        // Die Texte des Anbieters, nicht Einstellungen des Nutzers.
        section: 'system',
      },
    ],
  },

  // Subscription Plugin
  {
    name: '@xhubio-saas/core-frontend/plugin-subscription',
    version: '1.0.0',
    navigation: [
      {
        id: 'subscription',
        labelKey: 'subscription.nav.title',
        icon: 'CreditCard',
        href: '/subscription',
        order: 50,
        group: 'settings',
        // Reihenfolge Einstellungen: Benutzer(10) · Berechtigungen(20) · Finanzamt-Zugänge(30) · Einstellungen(40) · Abonnement(50) · Änderungsprotokoll(60)
        requiredEntitlement: 'billing:subscription:read',
      },
    ],
    routes: [
      {
        path: '/subscription',
        component: SubscriptionPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'billing:subscription:read',
        breadcrumb: {
          labelKey: 'subscription.nav.title',
        },
      },
    ],
    translations: {
      namespace: 'subscription',
      messages: {
        bg: subscriptionLocalesBg,
        cs: subscriptionLocalesCs,
        de: subscriptionLocalesDe,
        en: subscriptionLocalesEn,
        es: subscriptionLocalesEs,
        fr: subscriptionLocalesFr,
        hu: subscriptionLocalesHu,
        it: subscriptionLocalesIt,
        nl: subscriptionLocalesNl,
        pl: subscriptionLocalesPl,
        pt: subscriptionLocalesPt,
        ro: subscriptionLocalesRo,
      },
    },
  },

  // API Key Management Plugin
  {
    name: '@xhubio-saas/frontend-plugin-api-key',
    version: '1.0.0',
    navigation: [
      {
        id: 'API Keys',
        labelKey: 'apiKey.nav.title',
        icon: 'KeyRound',
        href: '/api-keys',
        order: 500,
        group: 'developers',
        requiredEntitlement: 'api-key:api-key:read',
      },
    ],
    routes: [
      {
        path: '/api-keys',
        component: ApiKeysPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'api-key:api-key:read',
        breadcrumb: {
          labelKey: 'apiKey.nav.title',
        },
      },
    ],
    translations: {
      namespace: 'apiKey',
      messages: {
        bg: apiKeyLocalesBg,
        cs: apiKeyLocalesCs,
        de: apiKeyLocalesDe,
        en: apiKeyLocalesEn,
        es: apiKeyLocalesEs,
        fr: apiKeyLocalesFr,
        hu: apiKeyLocalesHu,
        it: apiKeyLocalesIt,
        nl: apiKeyLocalesNl,
        pl: apiKeyLocalesPl,
        pt: apiKeyLocalesPt,
        ro: apiKeyLocalesRo,
      },
    },
    pageHelp: [
      {
        pageId: '/api-keys',
        namespace: 'apiKey',
        sections: [
          {
            id: 'overview',
            titleKey: 'help.overview.title',
            contentKey: 'help.overview.content',
            order: 10,
          },
          {
            id: 'create',
            titleKey: 'help.create.title',
            contentKey: 'help.create.content',
            order: 20,
          },
          {
            id: 'permissions',
            titleKey: 'help.permissions.title',
            contentKey: 'help.permissions.content',
            order: 30,
          },
          {
            id: 'security',
            titleKey: 'help.security.title',
            contentKey: 'help.security.content',
            order: 40,
          },
        ],
      },
    ],
  },

  // OpenAPI Documentation Plugin
  // The Scalar UI is served by the API backend at /docs (auth-gated). The
  // sidebar entry is an external link that opens /docs in a new browser tab —
  // no in-app route needed. Visible whenever the user is logged in; the
  // backend then personalizes the spec per the user's entitlements (or
  // shows an "upgrade" message if entitlements are empty).
  {
    name: '@xhubio-saas/saas-pdf-api-frontend/plugin-api-docs',
    version: '1.0.0',
    navigation: [
      {
        id: 'api-docs',
        labelKey: 'apiDocs.nav.title',
        icon: 'BookOpen',
        href: OPENAPI_DOCS_URL,
        external: true,
        order: 550,
        group: 'developers',
      },
    ],
    translations: {
      namespace: 'apiDocs',
      messages: {
        de: { nav: { title: 'OpenAPI Spec' } },
        en: { nav: { title: 'OpenAPI Spec' } },
      },
    },
  },

  // Usage Overview Plugin
  {
    name: '@xhubio-saas/core-frontend/plugin-usage',
    version: '1.0.0',
    navigation: [
      {
        id: 'usage',
        labelKey: 'usage.nav.title',
        icon: 'Activity',
        href: '/usage',
        order: 400,
        group: 'analytics',
        requiredEntitlement: 'billing:usage:read',
      },
    ],
    routes: [
      {
        path: '/usage',
        component: UsagePageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'billing:usage:read',
        breadcrumb: {
          labelKey: 'usage.nav.title',
        },
      },
    ],
    translations: {
      namespace: 'usage',
      messages: {
        bg: usageLocalesBg,
        cs: usageLocalesCs,
        de: usageLocalesDe,
        en: usageLocalesEn,
        es: usageLocalesEs,
        fr: usageLocalesFr,
        hu: usageLocalesHu,
        it: usageLocalesIt,
        nl: usageLocalesNl,
        pl: usageLocalesPl,
        pt: usageLocalesPt,
        ro: usageLocalesRo,
      },
    },
  },

  // PDF Template Editor Plugin
  {
    name: '@xhubio-saas/pdf-frontend/plugin-pdf',
    version: '1.0.0',
    navigation: [
      {
        id: 'pdf-templates',
        labelKey: 'pdf.templates.title',
        icon: 'FileType',
        href: '/pdf/v2/templates',
        order: 700,
        group: 'products',
        requiredEntitlement: 'pdf:template:read',
      },
    ],
    routes: [
      {
        path: '/pdf/v2/templates',
        component: PdfTemplateListV2PageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'pdf:template:read',
        breadcrumb: {
          labelKey: 'pdf.templates.title',
        },
      },
      {
        path: '/pdf/v2/templates/:id',
        component: PdfTemplateEditorV2Page,
        layout: 'protected',
        requiredEntitlement: 'pdf:template:read',
        breadcrumb: {
          labelKey: 'pdf.templates.edit',
        },
      },
      // Alt-URLs des abgeschalteten v1-Editors (Plan PDF-API 09-05/01).
      {
        path: '/pdf/templates',
        component: PdfTemplatesRedirect,
        layout: 'protected',
      },
      {
        path: '/pdf/templates/:id',
        component: PdfTemplateEditRedirect,
        layout: 'protected',
      },
    ],
    translations: {
      namespace: 'pdf',
      messages: {
        bg: pdfLocalesBg,
        cs: pdfLocalesCs,
        de: pdfLocalesDe,
        en: pdfLocalesEn,
        es: pdfLocalesEs,
        fr: pdfLocalesFr,
        hu: pdfLocalesHu,
        it: pdfLocalesIt,
        nl: pdfLocalesNl,
        pl: pdfLocalesPl,
        pt: pdfLocalesPt,
        ro: pdfLocalesRo,
      },
    },
  },

  // Playground Plugin - API Demo
  // {
  //   name: '@xhubio-saas/core-frontend/plugin-playground',
  //   version: '1.0.0',
  //   navigation: [
  //     {
  //       id: 'playground',
  //       labelKey: 'playground.nav.title',
  //       icon: 'Play',
  //       href: '/playground',
  //       order: 10,
  //       group: 'tools',
  //     },
  //   ],
  //   routes: [
  //     {
  //       path: '/playground',
  //       component: PlaygroundPageWithProviders,
  //       layout: 'protected',
  //       breadcrumb: {
  //         labelKey: 'playground.nav.title',
  //       },
  //     },
  //   ],
  //   translations: {
  //     namespace: 'playground',
  //     messages: {
  //       de: playgroundLocalesDe,
  //       en: playgroundLocalesEn,
  //     },
  //   },
  // },

  // Log Audit Plugin - Audit Log Viewer
  {
    name: '@xhubio-saas/core-frontend/plugin-log-audit',
    version: '1.0.0',
    navigation: [
      {
        id: 'log-audit',
        labelKey: 'logAudit.nav.title',
        icon: 'ClipboardList',
        href: '/log-audit',
        order: 410,
        group: 'analytics',
        requiredEntitlement: 'audit:log:read',
      },
    ],
    routes: [
      {
        path: '/log-audit',
        component: LogAuditPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'audit:log:read',
        breadcrumb: {
          labelKey: 'logAudit.nav.title',
        },
      },
    ],
    translations: {
      namespace: 'logAudit',
      messages: {
        bg: logAuditLocalesBg,
        cs: logAuditLocalesCs,
        de: logAuditLocalesDe,
        en: logAuditLocalesEn,
        es: logAuditLocalesEs,
        fr: logAuditLocalesFr,
        hu: logAuditLocalesHu,
        it: logAuditLocalesIt,
        nl: logAuditLocalesNl,
        pl: logAuditLocalesPl,
        pt: logAuditLocalesPt,
        ro: logAuditLocalesRo,
      },
    },
  },

  // Onboarding (YAML-driven post-registration flow).
  // Inline FrontendPlugin object — same wiring style as the API-Key plugin
  // above. The route component (OnboardingYamlPageWithProviders) lives in
  // app.pages.tsx and uses useTranslation() to pick the right DE/EN config
  // at render time, mirroring ApiKeysPageWithProviders.
  // No sidebar entry — purely post-reg, dismissed once
  // onboardingCompleted=true is written to user preferences.
  {
    name: '@xhubio-saas/frontend-plugin-onboarding-yaml',
    version: '1.0.0',
    routes: [
      {
        path: '/setup',
        component: OnboardingYamlPageWithProviders,
        layout: 'protected',
        breadcrumb: { labelKey: 'onboardingYaml:nav.title' },
      },
      {
        path: '/setup/thanks',
        component: OnboardingThanks,
        layout: 'protected',
        breadcrumb: { labelKey: 'onboardingYaml:nav.thanks' },
      },
    ],
    translations: {
      namespace: 'onboardingYaml',
      // Full nav/page/labels come from core-frontend (single source of truth);
      // nav.thanks is app-specific (the /setup/thanks route is wired here, not
      // by the factory), so layer it on top of the shared messages.
      messages: {
        ...onboardingYamlMessages,
        de: {
          ...onboardingYamlMessages.de,
          nav: { ...onboardingYamlMessages.de.nav, thanks: 'Danke' },
        },
        en: {
          ...onboardingYamlMessages.en,
          nav: { ...onboardingYamlMessages.en.nav, thanks: 'Thanks' },
        },
      },
    },
  },

  // Roles & Permissions Plugin.
  {
    name: '@xhubio-saas/core-frontend/plugin-roles',
    version: '1.0.0',
    // Roles is a top-level "Einstellungen" sidebar entry (not a settings tab) so
    // its page keeps the full-width layout instead of replacing the tab bar.
    // The /settings/roles route below renders the page.
    navigation: [
      {
        id: 'roles',
        labelKey: 'roles.settings.tab',
        icon: 'ShieldCheck',
        href: '/settings/roles',
        order: 20,
        group: 'settings',
        requiredEntitlement: 'rbac:role:read',
      },
    ],
    routes: [
      {
        path: '/settings/roles',
        component: RoleListPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'rbac:role:read',
        breadcrumb: { labelKey: 'roles.settings.tab' },
      },
      {
        path: '/settings/roles/:roleId',
        component: RoleDetailPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'rbac:role:read',
        breadcrumb: { labelKey: 'roles.detail.breadcrumb', parent: '/settings/roles' },
      },
    ],
    translations: {
      namespace: 'roles',
      messages: {
        bg: rolesLocalesBg,
        cs: rolesLocalesCs,
        de: rolesLocalesDe,
        en: rolesLocalesEn,
        es: rolesLocalesEs,
        fr: rolesLocalesFr,
        hu: rolesLocalesHu,
        it: rolesLocalesIt,
        nl: rolesLocalesNl,
        pl: rolesLocalesPl,
        pt: rolesLocalesPt,
        ro: rolesLocalesRo,
      },
    },
  },

  // Users / Team Plugin — invite users, manage members + their roles.
  {
    name: '@xhubio-saas/core-frontend/plugin-user',
    version: '1.0.0',
    navigation: [
      {
        id: 'users',
        labelKey: 'user.nav.title',
        icon: 'Users',
        href: '/users',
        order: 10,
        group: 'settings',
        requiredEntitlement: 'members:member:read',
      },
    ],
    routes: [
      {
        path: '/users',
        component: UserListPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'members:member:read',
        breadcrumb: { labelKey: 'user.nav.title' },
      },
      {
        path: '/users/:memberId',
        component: UserDetailPageWithProviders,
        layout: 'protected',
        requiredEntitlement: 'members:member:read',
        breadcrumb: { labelKey: 'user.detail.tab.profile', parent: '/users' },
      },
      {
        path: '/invite/accept',
        component: AcceptInvitationPageWithProviders,
        layout: 'public',
      },
      {
        path: '/verify-email-change',
        component: VerifyEmailChangePageWithProviders,
        layout: 'public',
      },
      {
        path: '/ownership-transfer/accept',
        component: AcceptOwnershipTransferPageWithProviders,
        layout: 'public',
      },
    ],
    translations: {
      namespace: 'user',
      messages: {
        bg: userLocalesBg,
        cs: userLocalesCs,
        de: userLocalesDe,
        en: userLocalesEn,
        es: userLocalesEs,
        fr: userLocalesFr,
        hu: userLocalesHu,
        it: userLocalesIt,
        nl: userLocalesNl,
        pl: userLocalesPl,
        pt: userLocalesPt,
        ro: userLocalesRo,
      },
    },
  },

  // Loeschfrist-Streifen (Plan CORE 09-25/08 S3) — der Kern-Streifen ueber jeder
  // geschuetzten Seite, solange die Kontoloeschung beantragt ist; Widerruf mit einem Klick.
  createAccountDeletionFrontendPlugin(),
]
